import { Alert, AlertDescription, AlertTitle, Button } from "@devhop/ui";
import { RefreshCw, Share2, ShieldAlert } from "lucide-react";
import type { FacebookPageListItem } from "../api/types";

type Props = {
  pages: FacebookPageListItem[];
  refreshing?: boolean;
  reconnecting?: boolean;
  onRefresh: () => void;
  onReconnect: () => void;
};

/**
 * แสดง alert เมื่อมี page ที่ token ใกล้หมดอายุ (expiring_soon) หรือหมดอายุ (expired/revoked)
 * - expiring_soon → ลอง refresh ก่อน
 * - expired/revoked → ต้อง re-OAuth
 */
export function FacebookTokenAlert({
  pages,
  refreshing,
  reconnecting,
  onRefresh,
  onReconnect,
}: Props) {
  const expiringSoon = pages.filter((p) => p.tokenStatus === "expiring_soon");
  const expired = pages.filter(
    (p) => p.tokenStatus === "expired" || p.tokenStatus === "revoked",
  );

  if (expiringSoon.length === 0 && expired.length === 0) return null;

  const hasExpired = expired.length > 0;
  const title = hasExpired
    ? `ມີ ${expired.length} Page ຕ້ອງເຊື່ອມຕໍ່ໃໝ່`
    : `ມີ ${expiringSoon.length} Page Token ໃກ້ໝົດອາຍຸ`;

  const description = hasExpired
    ? "Token ໝົດອາຍຸແລ້ວ — ກະລຸນາເຊື່ອມ Facebook ໃໝ່ ແລະ ເມື່ອ Facebook ຖາມໃຫ້ເລືອກ Page ຕ້ອງເລືອກ Page ທີ່ຈະໃຊ້ຮັບ comment ແລະ Messenger"
    : "ລະບົບກຳລັງພະຍາຍາມຣີເຟຣສ Token ໂດຍອັດຕະໂນມັດ — ກົດປຸ່ມຂ້າງລຸ່ມເພື່ອລອງດຽວນີ້";

  return (
    <Alert variant={hasExpired ? "destructive" : "default"} className="mb-6">
      <ShieldAlert className="size-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span>{description}</span>
        <div className="flex flex-wrap gap-2">
          {!hasExpired ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={refreshing}
              onClick={onRefresh}
            >
              <RefreshCw
                className={refreshing ? "size-4 animate-spin" : "size-4"}
              />
              ລອງຣີເຟຣສ Token
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant={hasExpired ? "default" : "secondary"}
            disabled={reconnecting}
            onClick={onReconnect}
          >
            <Share2 className="size-4" />
            ເຊື່ອມຕໍ່ Facebook ໃໝ່
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
